import type { ReactNode } from 'react';

import { IconAsterisk, IconCrossSmall } from '../../core/icons';

export type ActionDockCaptionPlacement = 'top' | 'bottom';

export type ActionDockProps = {
    readonly children: ReactNode;
    readonly caption?: ReactNode;
    readonly captionPlacement?: ActionDockCaptionPlacement;
    readonly className?: string;
};

export function ActionDock({ children, caption, captionPlacement = 'bottom', className }: ActionDockProps) {
    const captionNode = caption ? (
        <span className="text-xxs font-medium leading-snug text-black-500">{caption}</span>
    ) : null;

    return (
        <div className={`flex flex-col gap-s font-sans ${className ?? ''}`}>
            {captionPlacement === 'top' && captionNode}
            <div className="flex flex-col items-stretch gap-s">{children}</div>
            {captionPlacement === 'bottom' && captionNode}
        </div>
    );
}

export type ActionDockStatusPillStatus = 'ready' | 'loading' | 'cancelled';
export type ActionDockStatusPillTone = 'onPrimary' | 'default';

export type ActionDockStatusPillProps = {
    readonly status?: ActionDockStatusPillStatus;
    readonly tone?: ActionDockStatusPillTone;
    readonly label?: string;
    readonly className?: string;
};

const STATUS_LABEL: Record<ActionDockStatusPillStatus, string> = {
    ready: 'Drafted to match',
    loading: 'Drafting to match',
    cancelled: 'Cancelled',
};

function ActionDockStatusPill({ status = 'ready', tone = 'onPrimary', label, className }: ActionDockStatusPillProps) {
    const onPrimary = tone === 'onPrimary';
    const text = label ?? STATUS_LABEL[status];
    const textColor = status === 'cancelled' ? 'text-black-400' : onPrimary ? 'text-white-200' : 'text-black-500';
    const iconTint = status === 'cancelled' ? 'text-black-400' : onPrimary ? 'text-orange-300' : 'text-orange-800';
    const dotTint = onPrimary ? 'bg-orange-300' : 'bg-orange-600';

    return (
        <span className={`inline-flex items-center gap-xs text-xxs leading-snug ${textColor} ${className ?? ''}`}>
            {status === 'loading' ? (
                <span
                    aria-hidden={true}
                    className={`inline-block h-[8px] w-[8px] animate-pulse rounded-full ${dotTint}`}
                />
            ) : status === 'cancelled' ? (
                <IconCrossSmall size={10} className={iconTint} />
            ) : (
                <IconAsterisk size={10} className={iconTint} />
            )}
            {text}
        </span>
    );
}

ActionDock.StatusPill = ActionDockStatusPill;
